<script setup>
import { computed, onMounted, ref } from 'vue';
import AddAttribute from './AddAttribute.vue';
import CustomAttribute from './CustomAttribute.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

const { t } = useI18n();

const getters = useStoreGetters();
const store = useStore();

const showAddPopup = ref(false);
const selectedTabIndex = ref(0);
const uiFlags = computed(() => getters['attributes/getUIFlags'].value);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const tabs = computed(() => {
  return [
    {
      key: 0,
      name: t('ATTRIBUTES_MGMT.TABS.CONVERSATION'),
    },
    {
      key: 1,
      name: t('ATTRIBUTES_MGMT.TABS.CONTACT'),
    },
  ];
});

onMounted(() => {
  store.dispatch('attributes/get');
});

const attributeModel = computed(() =>
  selectedTabIndex.value ? 'contact_attribute' : 'conversation_attribute'
);

const attributes = computed(() =>
  getters['attributes/getAttributesByModel'].value(attributeModel.value)
);

const onClickTabChange = index => {
  selectedTabIndex.value = index;
};
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 56px"
    >
      <div class="flex items-center gap-3">
        <h1 class="text-xl font-semibold" style="color: #111827">
          {{ $t('ATTRIBUTES_MGMT.HEADER') }}
        </h1>
        <span class="ws-count-badge">{{ attributes.length }}</span>
      </div>
      <button class="ws-btn" @click="openAddPopup">
        <span class="i-ph-plus-bold size-4" />
        {{ $t('ATTRIBUTES_MGMT.HEADER_BTN_TXT') }}
      </button>
    </header>

    <!-- Description -->
    <div class="ws-desc px-6 py-3">
      <p class="text-sm" style="color: #4b5563; margin: 0">
        {{ $t('ATTRIBUTES_MGMT.DESCRIPTION') }}
      </p>
    </div>

    <!-- Tabs -->
    <div class="ws-tabs px-6">
      <div class="flex gap-0">
        <button
          v-for="(tab, index) in tabs"
          :key="tab.key"
          class="ws-tab"
          :class="{ 'ws-tab--active': selectedTabIndex === index }"
          @click="onClickTabChange(index)"
        >
          {{ tab.name }}
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      <!-- Loading State -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <woot-loading-state :message="$t('ATTRIBUTES_MGMT.LOADING')" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!attributes.length"
        class="flex items-center justify-center py-20"
      >
        <p class="text-n-slate-11">
          {{ $t('ATTRIBUTES_MGMT.LIST.EMPTY_RESULT.404') }}
        </p>
      </div>

      <!-- Attributes Content -->
      <div v-else class="p-6">
        <div class="ws-table-wrap">
          <CustomAttribute
            :key="attributeModel"
            :attribute-model="attributeModel"
          />
        </div>
      </div>
    </div>

    <!-- Add Attribute Modal -->
    <AddAttribute
      v-if="showAddPopup"
      v-model:show="showAddPopup"
      :on-close="hideAddPopup"
      :selected-attribute-model-tab="selectedTabIndex"
    />
  </div>
</template>

<style scoped>
.settings-page,
.dark .settings-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.ws-btn:hover {
  background: #1f2937;
}
.ws-count-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}
.ws-tabs {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  padding-top: 2px;
}
.ws-tab {
  border: none;
  background: transparent;
  color: #6b7280;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.15s ease;
}
.ws-tab:hover {
  color: #111827;
}
.ws-tab--active {
  color: #0066ff;
  border-bottom-color: #0066ff;
  font-weight: 600;
}
.ws-table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
}

/* Deep overrides for child components */
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-background) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
:deep(input:not(.reset-base)),
:deep(select:not(.reset-base)),
:deep(textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
:deep(label) {
  color: #111827 !important;
}
:deep(th) {
  color: #374151 !important;
  background: #f9fafb !important;
}
:deep(td) {
  color: #111827 !important;
}
:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}
:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form) {
  background: #ffffff !important;
}

/* legacy kept for compat */
.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--wlab-text-primary);
  line-height: 1;
}

.stat-label {
  font-size: 12px;
  color: var(--wlab-text-muted);
  margin-top: 4px;
}
</style>
